import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Stack, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  minWidth: 'auto'
}));

// Pixel-perfect grouped column layout styles adapted from user's snippet
const Card = styled('div')(({ theme }) => ({
  width: '100%',
  background: '#ffffff',
  borderRadius: 12,
  boxShadow: '0 12px 32px rgba(0,0,0,.08)',
  padding: 16,
  [theme.breakpoints.up('sm')]: {
    borderRadius: 14,
    padding: 20
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: 16,
    padding: 24
  }
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginBottom: 12,
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.up('sm')]: {
    fontSize: 16
  },
  [theme.breakpoints.up('md')]: {
    fontSize: 18
  }
}));

const Legend = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 12,
  color: '#6b7280',
  fontSize: 11,
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.up('sm')]: {
    gap: 16,
    fontSize: 12
  }
}));

const Badge = styled('span')({
  display: 'inline-flex',
  gap: 6,
  alignItems: 'center'
});

const Swatch = styled('span')(({ color }) => ({
  width: 12,
  height: 8,
  borderRadius: 999,
  background: color,
  display: 'inline-block'
}));

const Grid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 22,
  alignItems: 'end'
});

const Group = styled('div')({
  display: 'grid',
  gridTemplateRows: 'auto auto auto',
  alignItems: 'end',
  justifyItems: 'center',
  padding: '8px 6px 0',
  borderRadius: 12
});

const Bars = styled('div')({
  position: 'relative',
  height: 220,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  gap: 14,
  background: 'linear-gradient(to top, #eef2f7 1px, transparent 1px) top/100% 55px repeat-y',
  borderRadius: '10px 10px 0 0',
  padding: '0 6px 6px'
});

const Bar = styled('div')(({ color }) => ({
  width: 28,
  borderRadius: 8,
  position: 'relative',
  transformOrigin: 'bottom',
  transform: 'scaleY(0)',
  animation: 'grow .9s cubic-bezier(.2,.7,.2,1) forwards',
  background: color
}));

const BarWrap = styled('div')({
  position: 'relative',
  width: 28,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  height: '100%'
});

const ValueLabel = styled('div')({
  position: 'absolute',
  bottom: 'calc(100% + 6px)',
  left: '50%',
  transform: 'translateX(-50%)',
  fontSize: 12,
  color: '#111827',
  whiteSpace: 'nowrap',
  fontFamily: 'Inter, sans-serif'
});

const Name = styled('div')({
  marginTop: 10,
  fontWeight: 600,
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif',
  color: '#111827'
});

const Sub = styled('div')({
  marginTop: 2,
  fontSize: 12,
  color: '#6b7280',
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif'
});

// Inject keyframes for animation
const GlobalStyles = styled('style')``;

const ChartWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch'
}));

const ComparisonChartModal = ({ open, onClose, title, currentValue = 0, lastYearValue = 0 }) => {
  const safeCurrent = Number.isFinite(currentValue) ? currentValue : 0;
  const safeLast = Number.isFinite(lastYearValue) ? lastYearValue : 0;
  const [chartWidth, setChartWidth] = useState(300);
  const chartContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Responsive chart dimensions
  useEffect(() => {
    const updateChartWidth = () => {
      if (chartContainerRef.current) {
        const containerWidth = chartContainerRef.current.offsetWidth;
        const padding = isMobile ? 32 : isTablet ? 40 : 48;
        const calculatedWidth = Math.max(280, containerWidth - padding);
        setChartWidth(calculatedWidth);
      }
    };

    // Delay initial measurement to ensure modal is fully rendered
    const timeoutId = setTimeout(updateChartWidth, 100);

    const resizeObserver = new ResizeObserver(updateChartWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    window.addEventListener('resize', updateChartWidth);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateChartWidth);
    };
  }, [isMobile, isTablet, open]);

  // Generate sample data points for line chart comparison
  const chartData = useMemo(() => {
    // Generate last 5 months from current date (dynamic based on current month)
    const currentDate = new Date();
    const months = [];
    
    // Create array of last 5 months including current month
    // Example: If current month is September 2025, shows: May, Jun, Jul, Aug, Sep
    for (let i = 4; i >= 0; i--) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
      months.push(monthName);
    }
    
    const lastYearData = [];
    const currentYearData = [];
    
    // Generate realistic progression data
    for (let i = 0; i < 5; i++) {
      const lastYearPoint = safeLast * (0.6 + (i * 0.1)); // Gradual increase
      const currentYearPoint = safeCurrent * (0.5 + (i * 0.125)); // Different progression
      
      lastYearData.push(Math.round(lastYearPoint));
      currentYearData.push(Math.round(currentYearPoint));
    }
    
    return {
      xAxisData: months,
      series: [
        {
          data: lastYearData,
          color: '#EF232A',
          label: 'Last Year',
          curve: 'linear'
        },
        {
          data: currentYearData,
          color: '#34A853',
          label: 'This Year',
          curve: 'linear'
        }
      ]
    };
  }, [safeCurrent, safeLast]);

  // Calculate max value for Y-axis
  const maxValue = useMemo(() => {
    const allValues = [...chartData.series[0].data, ...chartData.series[1].data];
    return Math.max(...allValues, 100);
  }, [chartData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: isMobile ? 16 : 20 }}>{title || 'Comparison'}</DialogTitle>
      <DialogContent>
        <Container ref={chartContainerRef}>
          <GlobalStyles>{`
            @keyframes grow { to { transform: scaleY(1); } }
          `}</GlobalStyles>
          <Card>
            <Header>
              <Title>Last 5 Months Performance Trend</Title>
              <Legend>
                <Badge><Swatch color="#34A853" />This Year</Badge>
                <Badge><Swatch color="#EF232A" />Last Year</Badge>
              </Legend>
            </Header>

            <ChartWrapper>
              <LineChart
                width={chartWidth}
                height={isMobile ? 250 : isTablet ? 300 : 350}
                series={chartData.series}
                xAxis={[{
                  scaleType: 'point',
                  data: chartData.xAxisData,
                  tickLabelStyle: {
                    fontSize: isMobile ? 10 : 12
                  }
                }]}
                yAxis={[{
                  min: 0,
                  max: Math.ceil(maxValue * 1.1),
                  tickNumber: isMobile ? 5 : 6
                }]}
                margin={{
                  left: isMobile ? 50 : 70,
                  right: isMobile ? 20 : 50,
                  top: isMobile ? 30 : 50,
                  bottom: isMobile ? 50 : 80
                }}
                grid={{ horizontal: true, vertical: false }}
                legend={{
                  position: { vertical: 'top', horizontal: 'right' },
                  padding: 5,
                  hidden: isMobile
                }}
              />
            </ChartWrapper>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#111827', fontSize: isMobile ? 14 : 18 }}>
                {title || 'Metric'}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', color: '#6b7280', fontSize: isMobile ? 11 : 14 }}>
                Current: {safeCurrent.toLocaleString()} | Last Year: {safeLast.toLocaleString()}
              </Typography>
            </Box>
          </Card>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', background: '#0B91D6' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComparisonChartModal;
